/** The spine and its rows.
 *
 *  A CSS grid, not `@mui/lab`'s `<Timeline>`: that component is ~64px per node
 *  with fixed left/right alternation, which is the opposite of the density this
 *  needs, and it fights sticky milestones and collapsed runs. Three columns —
 *  offset label · rail · content — keep every row aligned on one vertical line
 *  at any density.
 */
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';

import { ValueActions } from '@/components/ValueActions/ValueActions';

import { eventActions, eventFiresActions } from '../notifications/notificationLinks';
import { NotificationCard } from './NotificationCard';
import { DeliveryMethodChips } from './DeliveryMethodChips';
import { familyBadge, familyName, formatFires, isLive } from './format';
import type {
  Density,
  TimelineActions,
  TimelineEventNode,
  TimelineNotification,
  TimelineRow,
  TimelineSlot,
  TimelineStats,
} from './model/types';
import { TOUR_OVERRIDE_GROUPS } from './format';
import { WarningBadges } from './WarningBadges';

/** Grid template shared by every row so the rail is one unbroken line:
 *  offset label · rail · content.
 *
 *  Bands with no time axis (reactive, unknown) drop the first two columns
 *  entirely — an offset column there would be an empty gutter, and printing a
 *  label in it would be inventing a position those events do not have. */
export const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: '5.5rem 1.25rem minmax(0, 1fr)',
  columnGap: 1,
  alignItems: 'stretch',
} as const;

const PLAIN_ROW = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  alignItems: 'stretch',
} as const;

function rowGrid(showRail: boolean) {
  return showRail ? ROW_GRID : PLAIN_ROW;
}

interface RowsProps {
  rows: TimelineRow[];
  milestones: Record<number, string>;
  density: Density;
  actions?: TimelineActions;
  stats?: TimelineStats;
  onInspect?: (nf: TimelineNotification) => void;
  busyNotificationIds?: ReadonlySet<number>;
  busyMethodIds?: ReadonlySet<number>;
  busyEvents?: ReadonlySet<string>;
  /** Rail is drawn only for the lifecycle band; other bands are plain lists. */
  showRail?: boolean;
  /** Inside a family group the derived "Order" / "Booking (per guest)" label
   *  just repeats the group heading — drop it and let the code carry the row.
   *  Curated names (e.g. "Tour participant - trip is about to begin") stay. */
  hideEventLabelWhenFamily?: boolean;
  /** The operator on screen — scopes the event rows' grid links. */
  tenantId?: number | null;
}

export function TimelineRows({
  rows,
  milestones,
  density,
  actions,
  stats,
  onInspect,
  busyNotificationIds,
  busyMethodIds,
  busyEvents,
  showRail = true,
  hideEventLabelWhenFamily = false,
  tenantId = null,
}: RowsProps) {
  return (
    <Box>
      {rows.map((row, index) => (
        <Fragment key={row.kind === 'slot' ? row.key : row.key}>
          {milestones[index] && <Milestone label={milestones[index]!} showRail={showRail} />}
          {row.kind === 'empty-run' ? (
            <EmptyRunRow
              fromLabel={row.fromLabel}
              toLabel={row.toLabel}
              count={row.slots.length}
              slots={row.slots}
              density={density}
              actions={actions}
              showRail={showRail}
            />
          ) : (
            <SlotRow
              slot={row}
              density={density}
              actions={actions}
              stats={stats}
              onInspect={onInspect}
              busyNotificationIds={busyNotificationIds}
              busyMethodIds={busyMethodIds}
              busyEvents={busyEvents}
              showRail={showRail}
              hideEventLabelWhenFamily={hideEventLabelWhenFamily}
              tenantId={tenantId}
            />
          )}
        </Fragment>
      ))}
    </Box>
  );
}

/** BOOKING / TRIP START / TRIP END — the three fixed points of the journey. */
function Milestone({ label, showRail }: { label: string; showRail: boolean }) {
  return (
    <Box sx={{ ...rowGrid(showRail), my: 0.5 }}>
      <Box />
      <Rail showRail={showRail} variant="milestone" />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ flex: 1, borderTop: '1px dashed', borderColor: 'divider' }} />
      </Stack>
    </Box>
  );
}

function Rail({
  showRail,
  variant = 'line',
}: {
  showRail: boolean;
  variant?: 'line' | 'milestone' | 'node' | 'ghost';
}) {
  if (!showRail) return <Box />;
  return (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1px', bgcolor: 'divider', height: '100%' }} />
      {variant !== 'line' && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            width: variant === 'milestone' ? 9 : 7,
            height: variant === 'milestone' ? 9 : 7,
            borderRadius: variant === 'milestone' ? 0 : '50%',
            transform: variant === 'milestone' ? 'rotate(45deg)' : undefined,
            bgcolor: variant === 'ghost' ? 'background.paper' : 'primary.main',
            border: variant === 'ghost' ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        />
      )}
    </Box>
  );
}

interface SlotRowProps {
  slot: TimelineSlot;
  density: Density;
  actions?: TimelineActions;
  stats?: TimelineStats;
  onInspect?: (nf: TimelineNotification) => void;
  busyNotificationIds?: ReadonlySet<number>;
  busyMethodIds?: ReadonlySet<number>;
  busyEvents?: ReadonlySet<string>;
  showRail: boolean;
  hideEventLabelWhenFamily?: boolean;
  tenantId?: number | null;
}

function SlotRow({ slot, density, showRail, ...rest }: SlotRowProps) {
  return (
    <Box sx={rowGrid(showRail)}>
      {showRail && (
        <>
          <Box sx={{ pt: 0.5, textAlign: 'right', pr: 0.5 }}>
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.8125rem',
                color: slot.empty ? 'text.disabled' : 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {slot.position.labelShort}
            </Typography>
          </Box>
          <Rail showRail variant={slot.empty ? 'ghost' : 'node'} />
        </>
      )}
      <Box sx={{ minWidth: 0, pb: slot.empty ? 0 : 0.5 }}>
        {slot.nodes.map((node) => (
          <EventNodeRow
            key={node.code}
            node={node}
            slotKey={slot.key}
            density={density}
            {...rest}
          />
        ))}
      </Box>
    </Box>
  );
}

interface NodeProps extends Omit<SlotRowProps, 'slot' | 'showRail'> {
  node: TimelineEventNode;
  slotKey: string;
}

function EventNodeRow({
  node,
  slotKey,
  density,
  actions,
  stats,
  onInspect,
  busyNotificationIds,
  busyMethodIds,
  busyEvents,
  hideEventLabelWhenFamily,
  tenantId = null,
}: NodeProps) {
  const [expanded, setExpanded] = useState(false);
  const count = node.notifications.length;
  const liveCount = node.notifications.filter(isLive).length;
  const empty = count === 0;
  const busy = busyEvents?.has(node.code);

  // Compact keeps every node on one line; detailed opens the populated ones.
  const showCards = density === 'detailed' ? !empty : expanded;

  const nodeFires = stats?.available
    ? node.notifications.reduce(
        (sum, nf) => sum + (nf.id === null ? 0 : (stats.byNotification[String(nf.id)]?.fires ?? 0)),
        0,
      )
    : undefined;

  return (
    <Box sx={{ py: 0.25 }}>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          alignItems: 'center',
          minHeight: 24,
          minWidth: 0,
          opacity: empty ? 0.55 : 1,
          cursor: empty || density === 'detailed' ? 'default' : 'pointer',
        }}
        onClick={empty || density === 'detailed' ? undefined : () => setExpanded((v) => !v)}
      >
        <Tooltip title={familyName(node.family)}>
          <Chip
            size="small"
            label={familyBadge(node.family)}
            variant="outlined"
            sx={{
              height: 18,
              minWidth: 26,
              fontSize: '0.6875rem',
              fontWeight: 700,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          />
        </Tooltip>

        <Typography
          variant="body2"
          noWrap
          sx={{ color: empty ? 'text.disabled' : 'text.primary', minWidth: 0 }}
        >
          {!(hideEventLabelWhenFamily && node.labelIsFamilyName) && `${node.label} `}
          {/* The code is what you configure and search by, so keep it on the
              row — muted and monospaced so the name still leads — and let it
              reach the notifications configured on it and what it delivered. */}
          <ValueActions actions={eventActions(node.code, { tenantId })}>
            <Box
              component="span"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.6875rem',
                color: 'text.disabled',
              }}
            >
              [{node.code}]
            </Box>
          </ValueActions>
        </Typography>

        {count > 0 && (
          // The count used to carry a tooltip; the popover would cover it, so
          // the same sentence becomes the popover's caption (components.md,
          // "use title instead of a hover tooltip on a value trigger").
          <ValueActions
            chip
            title={
              liveCount === count
                ? `${count} notification${count === 1 ? '' : 's'}`
                : `${liveCount} of ${count} will actually send`
            }
            actions={eventActions(node.code, { tenantId })}
          >
            <Chip
              size="small"
              label={liveCount === count ? count : `${liveCount}/${count}`}
              sx={{ height: 18, fontSize: '0.6875rem', fontWeight: 600 }}
              color={liveCount === 0 ? 'default' : 'primary'}
              variant={liveCount === 0 ? 'outlined' : 'filled'}
            />
          </ValueActions>
        )}

        {node.canFire === false && (
          <WarningBadges
            warnings={['event_cannot_fire']}
            cannotFireReason={node.cannotFireReason}
            canonicalEvent={node.canonicalCode}
            iconOnly
          />
        )}

        {/* Compact rolls the node's channels into one dot strip. */}
        {density === 'compact' && count > 0 && !expanded && (
          <DeliveryMethodChips methods={mergeMethods(node.notifications)} dense />
        )}

        <Box sx={{ flex: 1 }} />

        {nodeFires !== undefined && count > 0 && (
          <ValueActions
            title={`${nodeFires} sends in the last 30 days`}
            actions={eventFiresActions(node.code, { tenantId })}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: nodeFires === 0 ? 'text.disabled' : 'text.secondary',
              }}
            >
              {formatFires(nodeFires)}
            </Typography>
          </ValueActions>
        )}

        {count > 0 && actions?.onToggleEvent && (
          <Tooltip
            title={
              liveCount > 0
                ? 'Disable every notification on this event'
                : 'Enable every notification on this event'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  void actions.onToggleEvent!(node.code, liveCount === 0);
                }}
                aria-label={`${liveCount > 0 ? 'Disable' : 'Enable'} all on ${node.code}`}
              >
                <UnfoldMoreIcon
                  fontSize="small"
                  sx={{ fontSize: 16, transform: 'rotate(90deg)' }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {actions?.onCreateNotification && (
          <Tooltip title={`Add a notification on ${node.code}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                actions.onCreateNotification!({ eventCode: node.code, slotKey });
              }}
              aria-label={`Add a notification on ${node.code}`}
            >
              <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {density === 'compact' && count > 0 && (
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              fontSize: 16,
              color: 'text.disabled',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 120ms',
            }}
          />
        )}
      </Stack>

      {showCards && (
        <Box sx={{ pl: 3.5, pt: 0.25 }}>
          <NotificationGroups
            notifications={node.notifications}
            showDetail={density === 'detailed'}
            actions={actions}
            stats={stats}
            onInspect={onInspect}
            busyNotificationIds={busyNotificationIds}
            busyMethodIds={busyMethodIds}
          />
        </Box>
      )}
    </Box>
  );
}

/** Always / Default / Tour-specific, in that order. Tour-specific collapses:
 *  it is usually a long tail of per-tour variants nobody scans by default. */
function NotificationGroups({
  notifications,
  showDetail,
  actions,
  stats,
  onInspect,
  busyNotificationIds,
  busyMethodIds,
}: {
  notifications: TimelineNotification[];
  showDetail?: boolean;
  actions?: TimelineActions;
  stats?: TimelineStats;
  onInspect?: (nf: TimelineNotification) => void;
  busyNotificationIds?: ReadonlySet<number>;
  busyMethodIds?: ReadonlySet<number>;
}) {
  const [showOverrides, setShowOverrides] = useState(false);

  const card = (nf: TimelineNotification) => (
    <NotificationCard
      key={nf.key}
      notification={nf}
      showDetail={showDetail}
      actions={actions}
      stats={
        stats?.available && nf.id !== null ? stats.byNotification[String(nf.id)] : undefined
      }
      onInspect={onInspect}
      busyNotificationIds={busyNotificationIds}
      busyMethodIds={busyMethodIds}
    />
  );

  return (
    <>
      {TOUR_OVERRIDE_GROUPS.map(({ mode, label, help }) => {
        const group = notifications.filter((n) => (n.tourOverrideMode ?? 1) === mode);
        if (!group.length) return null;
        const collapsible = mode === 2;
        return (
          <Box key={mode} sx={{ mb: 0.25 }}>
            <Tooltip title={help}>
              <Typography
                component="div"
                onClick={collapsible ? () => setShowOverrides((v) => !v) : undefined}
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  cursor: collapsible ? 'pointer' : 'default',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {label}
                {collapsible && ` (${group.length})`}
                {collapsible && (
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 14,
                      transform: showOverrides ? 'rotate(180deg)' : 'none',
                      transition: 'transform 120ms',
                    }}
                  />
                )}
              </Typography>
            </Tooltip>
            {(!collapsible || showOverrides) && group.map(card)}
          </Box>
        );
      })}
      {/* Anything with an unexpected mode still renders rather than vanishing. */}
      {notifications
        .filter((n) => ![0, 1, 2].includes(n.tourOverrideMode ?? 1))
        .map(card)}
    </>
  );
}

/** Union of the channels used across a node's notifications, for the compact
 *  dot strip. A channel counts as enabled if ANY notification sends on it. */
function mergeMethods(notifications: TimelineNotification[]) {
  const byMethod = new Map<number | string, { method: number | null; label: string; on: boolean }>();
  let sawUndecided = false;
  for (const nf of notifications) {
    if (nf.deliveryMethods === undefined) {
      sawUndecided = true;
      continue;
    }
    for (const dm of nf.deliveryMethods) {
      const key = dm.method ?? dm.methodLabel;
      const prev = byMethod.get(key);
      byMethod.set(key, {
        method: dm.method,
        label: dm.methodLabel,
        on: (prev?.on ?? false) || dm.enabled !== false,
      });
    }
  }
  if (!byMethod.size && sawUndecided) return undefined;
  return [...byMethod.values()].map((m) => ({
    id: null,
    method: m.method,
    methodLabel: m.label,
    enabled: m.on,
  }));
}

function EmptyRunRow({
  fromLabel,
  toLabel,
  count,
  slots,
  density,
  actions,
  showRail,
}: {
  fromLabel: string;
  toLabel: string;
  count: number;
  slots: TimelineSlot[];
  density: Density;
  actions?: TimelineActions;
  showRail: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (open) {
    return (
      <>
        <Box sx={rowGrid(showRail)}>
          <Box />
          <Rail showRail={showRail} />
          <Typography
            variant="caption"
            onClick={() => setOpen(false)}
            sx={{ color: 'text.disabled', cursor: 'pointer', userSelect: 'none' }}
          >
            hide {count} unused events
          </Typography>
        </Box>
        {slots.map((slot) => (
          <SlotRow
            key={slot.key}
            slot={slot}
            density={density}
            actions={actions}
            showRail={showRail}
          />
        ))}
      </>
    );
  }

  return (
    <Box sx={rowGrid(showRail)}>
      <Box />
      <Rail showRail={showRail} />
      <Typography
        variant="caption"
        onClick={() => setOpen(true)}
        sx={{
          color: 'text.disabled',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        ⋯ {count} unused events ({fromLabel} → {toLabel})
      </Typography>
    </Box>
  );
}
